import axios from "../utils/axios";

export const getSetupIntentCRM = async (userId: number, signal?: AbortSignal) => {
  try {
    const response = await axios.post("/api/payment-methods/setup-intent/crm", { userId }, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

interface login {
  password: string;
  email: string;
}
interface giftsPayload {
  page: number;
  limit: number;
  search: string;
  sortBy: string;
  status: string;
  dateRange: string;
  clubId: string;
  type: string;
  amountRange: string;
  minAmount: number;
  maxAmount: number;
  startDate: string;
  endDate: string;
  fundId: string;
}

interface donorsPayload {
  page: number;
  limit: number;
  userType: string;
  isActive: boolean;
  location: string;
  frequency: string;
}

interface volunteersPayload {
  page: number;
  limit: number;
  search?: string;
  eventId?: number;
  type?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: string;
}

export const userLogin = async (payload: login, signal?: AbortSignal) => {
  try {
    const response = await axios.post("/api/auth/crm-login", payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getClubs = async (signal?: AbortSignal) => {
  try {
    const response = await axios.get('/api/clubs', { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getFunds = async (clubId?: number | null, signal?: AbortSignal) => {
  try {
    const url = clubId ? `/api/funds?clubId=${clubId}` : '/api/funds/all';
    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getAllEvents = async (signal?: AbortSignal) => {
  try {
    const response = await axios.get('/api/events/all', { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getCampaigns = async (signal?: AbortSignal) => {
  try {
    const response = await axios.get('/api/campaigns/all', { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getCrowdfundingsAll = async (signal?: AbortSignal) => {
  try {
    const response = await axios.get('/api/crowdfunding/all', { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const userGifts = async (payload?: Partial<giftsPayload>, signal?: AbortSignal) => {
  try {
    // Build query parameters from payload
    const params = new URLSearchParams();

    if (payload) {
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `/api/admin-gifts?${queryString}` : '/api/admin-gifts';

    console.log('API URL:', url);
    console.log('API Payload:', payload);

    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getDonors = async (payload?: Partial<donorsPayload>, signal?: AbortSignal) => {
  try {
    // Build query parameters from payload
    const params = new URLSearchParams();

    if (payload) {
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `/api/users?${queryString}` : '/api/users';

    console.log('API URL:', url);
    console.log('API Payload:', payload);

    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getDonorDetails = async (id: number, signal?: AbortSignal) => {
  try {

    const url = `/api/users/${id}`;
    console.log('API URL:', url);
    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Update contact profile (tags, summary, notes, userType)
export interface UpdateContactProfilePayload {
  contactTags?: string[];
  summary?: string;
  notes?: string;
  userType?: 'individual' | 'organization';
  deceased?: boolean;
  email?: string;
  phoneNumber?: string;
  firstName?: string;
  lastName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
}

export const updateContactProfile = async (id: number, payload: UpdateContactProfilePayload, signal?: AbortSignal) => {
  try {
    const url = `/api/users/${id}/contact-profile`;
    console.log('Update Contact Profile API URL:', url);
    console.log('Update Contact Profile Payload:', payload);
    const response = await axios.put(url, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Reset donor password
export const resetDonorPassword = async (id: number, newPassword: string, signal?: AbortSignal) => {
  try {
    const url = `/api/users/${id}/reset-password`;
    console.log('Reset Password API URL:', url);
    const response = await axios.post(url, { newPassword }, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Get user communications
export const getUserCommunications = async (userId: number, page = 1, limit = 10, signal?: AbortSignal) => {
  try {
    const url = `/api/users/${userId}/communications?page=${page}&limit=${limit}`;
    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Add communication
export interface AddCommunicationPayload {
  type: 'email' | 'phone' | 'faceToFace' | 'marketing' | 'letter' | 'sms' | 'other';
  subject?: string;
  notes?: string;
  direction: 'received' | 'initiated';
  date: string;
}

export const addCommunication = async (userId: number, payload: AddCommunicationPayload, signal?: AbortSignal) => {
  try {
    const url = `/api/users/${userId}/communications`;
    const response = await axios.post(url, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Get user engagements
export const getUserEngagements = async (
  userId: number,
  type: 'all' | 'communications' | 'events' | 'volunteering' = 'all',
  page = 1,
  limit = 10,
  signal?: AbortSignal
) => {
  try {
    const url = `/api/users/${userId}/engagements?type=${type}&page=${page}&limit=${limit}`;
    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Get user consents
export const getUserConsents = async (userId: number, signal?: AbortSignal) => {
  try {
    const url = `/api/users/${userId}/consents`;
    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Update user consents
export interface UpdateConsentsPayload {
  consents: Array<{
    type: 'faceToFace' | 'email' | 'emailMarketing' | 'mail' | 'sms' | 'socialMedia' | 'phone' | 'phoneMarketing';
    status: boolean;
  }>;
}

export const updateUserConsents = async (userId: number, payload: UpdateConsentsPayload, signal?: AbortSignal) => {
  try {
    const url = `/api/users/${userId}/consents`;
    const response = await axios.put(url, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Update source medium
export interface UpdateSourcePayload {
  sourceMedium?: string;
  sourceDetails?: string;
}

export const updateSourceMedium = async (userId: number, payload: UpdateSourcePayload, signal?: AbortSignal) => {
  try {
    const url = `/api/users/${userId}/source`;
    const response = await axios.put(url, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Get user relationships
export const getUserRelationships = async (userId: number, signal?: AbortSignal) => {
  try {
    const url = `/api/users/${userId}/relationships`;
    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Create relationship
export interface CreateRelationshipPayload {
  relatedUserId: number;
  relationshipType: string;
  isPrimaryContact?: boolean;
  contactType?: string;
  jobTitle?: string;
  department?: string;
  startDate?: string;
  endDate?: string;
  notes?: string;
}

export const createRelationship = async (userId: number, payload: CreateRelationshipPayload, signal?: AbortSignal) => {
  try {
    const url = `/api/users/${userId}/relationships`;
    const response = await axios.post(url, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Update relationship
export interface UpdateRelationshipPayload {
  relationshipType?: string;
  isPrimaryContact?: boolean;
  contactType?: string;
  jobTitle?: string;
  department?: string;
  startDate?: string;
  endDate?: string;
  notes?: string;
}

export const updateRelationship = async (relationshipId: number, payload: UpdateRelationshipPayload, signal?: AbortSignal) => {
  try {
    const url = `/api/users/relationships/${relationshipId}`;
    const response = await axios.put(url, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Delete relationship
export const deleteRelationship = async (relationshipId: number, signal?: AbortSignal) => {
  try {
    const url = `/api/users/relationships/${relationshipId}`;
    const response = await axios.delete(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Upload relationship document
export const uploadRelationshipDocument = async (
  relationshipId: number,
  file: File,
  documentType: string,
  signal?: AbortSignal
) => {
  try {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', documentType);

    const url = `/api/users/relationships/${relationshipId}/documents`;
    const response = await axios.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      signal,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// Delete relationship document
export const deleteRelationshipDocument = async (
  relationshipId: number,
  filename: string,
  signal?: AbortSignal
) => {
  try {
    const url = `/api/users/relationships/${relationshipId}/documents/${encodeURIComponent(filename)}`;
    const response = await axios.delete(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Search users for relationship linking
export const searchUsers = async (query: string, signal?: AbortSignal) => {
  try {
    const url = `/api/users/search?q=${encodeURIComponent(query)}`;
    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getVolunteers = async (payload?: Partial<volunteersPayload>, signal?: AbortSignal) => {
  try {
    // Build query parameters from payload
    const params = new URLSearchParams();

    if (payload) {
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `/api/volunteers/list?${queryString}` : '/api/volunteers/list';

    console.log('API URL:', url);
    console.log('API Payload:', payload);

    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

interface BulkVolunteerStatusUpdatePayload {
  volunteerIds: number[];
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
}

export const updateVolunteersBulkStatus = async (payload: BulkVolunteerStatusUpdatePayload, signal?: AbortSignal) => {
  try {
    const url = '/api/volunteers/bulk-status-update';
    console.log('Bulk Status Update API URL:', url);
    console.log('Bulk Status Update Payload:', payload);

    const response = await axios.put(url, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getUserDonations = async (id: number, payload?: { page?: number; limit?: number; search?: string }, signal?: AbortSignal) => {
  try {
    // Build query parameters from payload
    const params = new URLSearchParams();

    if (payload) {
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null && (typeof value === 'string' ? value !== '' : true)) {
          params.append(key, String(value));
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `/api/users/${id}/gift-campaigns?${queryString}` : `/api/users/${id}/gift-campaigns`;
    console.log('API URL:', url);
    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getFundGiftCampaigns = async (id: number, payload?: { page?: number; limit?: number; search?: string }, signal?: AbortSignal) => {
  try {
    // Build query parameters from payload
    const params = new URLSearchParams();

    if (payload) {
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null && (typeof value === 'string' ? value !== '' : true)) {
          params.append(key, String(value));
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `/api/funds/${id}/gift-campaigns?${queryString}` : `/api/funds/${id}/gift-campaigns`;
    console.log('Fund Gift Campaigns API URL:', url);
    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getDonationDetails = async (id: number, signal?: AbortSignal) => {
  try {

    const url = `/api/admin-gifts/${id}`;
    console.log('API URL:', url);
    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

interface RefundPayload {
  giftId: number;
  reason?: string;
  amount?: number;
}

export const refundGift = async (payload: RefundPayload, signal?: AbortSignal) => {
  try {
    const url = '/api/refund-gift';
    console.log('Refund API URL:', url);
    console.log('Refund Payload:', payload);

    const response = await axios.post(url, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

interface CancelGiftPayload {
  status: string;
}

export const cancelGift = async (giftId: number, payload: CancelGiftPayload, signal?: AbortSignal) => {
  try {
    const url = `/api/admin-gifts/${giftId}/status`;
    console.log('Cancel Gift API URL:', url);
    console.log('Cancel Gift Payload:', payload);

    const response = await axios.post(url, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

interface clubsPayload {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  fundId?: string;
  days?: string;
}

export const getClubsList = async (payload?: Partial<clubsPayload>, signal?: AbortSignal) => {
  try {
    // Build query parameters from payload
    const params = new URLSearchParams();

    if (payload) {
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `/api/admin-clubs/list?${queryString}` : '/api/admin-clubs/list';

    console.log('API URL:', url);
    console.log('API Payload:', payload);

    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

interface BulkStatusUpdatePayload {
  clubIds: number[];
  status: 'active' | 'inactive' | 'archived';
}

export const updateClubsBulkStatus = async (payload: BulkStatusUpdatePayload, signal?: AbortSignal) => {
  try {
    const url = '/api/admin-clubs/bulk-status';
    console.log('Bulk Status Update API URL:', url);
    console.log('Bulk Status Update Payload:', payload);

    const response = await axios.put(url, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

interface campaignsPayload {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: string;
}

export const getCampaignsList = async (payload?: Partial<campaignsPayload>, signal?: AbortSignal) => {
  try {
    // Build query parameters from payload
    const params = new URLSearchParams();

    if (payload) {
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `/api/campaigns/list?${queryString}` : '/api/campaigns/list';

    console.log('Campaigns API URL:', url);
    console.log('Campaigns API Payload:', payload);

    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

interface BulkCampaignStatusUpdatePayload {
  campaignIds: number[];
  status: 'active' | 'inactive' | 'archived';
}

export const updateCampaignsBulkStatus = async (payload: BulkCampaignStatusUpdatePayload, signal?: AbortSignal) => {
  try {
    const url = '/api/campaigns/bulk-status';
    console.log('Bulk Status Update API URL:', url);
    console.log('Bulk Status Update Payload:', payload);

    const response = await axios.post(url, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

interface fundsPayload {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  campaignId?: string;
  days?: string;
}

export const getFundsList = async (payload?: Partial<fundsPayload>, signal?: AbortSignal) => {
  try {
    // Build query parameters from payload
    const params = new URLSearchParams();

    if (payload) {
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `/api/funds/list?${queryString}` : '/api/funds/list';

    console.log('Funds API URL:', url);
    console.log('Funds API Payload:', payload);

    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

interface BulkFundStatusUpdatePayload {
  fundIds: number[];
  status: 'active' | 'inactive' | 'archived';
}

export const updateFundsBulkStatus = async (payload: BulkFundStatusUpdatePayload, signal?: AbortSignal) => {
  try {
    const url = '/api/funds/bulk-status';
    console.log('Bulk Status Update API URL:', url);
    console.log('Bulk Status Update Payload:', payload);

    const response = await axios.post(url, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Create Fund API
type CreateFundPayload = FormData | {
  name: string;
  description?: string;
  image?: string;
  campaignId: number;
  isActive?: boolean;
  isArchived?: boolean;
  icon?: string;
  isDefault?: boolean;
};

export const createFund = async (payload: CreateFundPayload, signal?: AbortSignal) => {
  try {
    let config: any = { signal };

    if (payload instanceof FormData) {
      // For FormData, override default Content-Type to let browser set it with boundary
      config.headers = {
        'Content-Type': 'multipart/form-data',
      };
    }

    const response = await axios.post('/api/funds', payload, config);
    return response;
  } catch (error) {
    throw error;
  }
};

// Update Fund API
type UpdateFundPayload = FormData | {
  name?: string;
  description?: string;
  image?: string;
  campaignId?: number;
  isActive?: boolean;
  isArchived?: boolean;
  icon?: string;
  isDefault?: boolean;
};

export const getFundById = async (fundId: number, signal?: AbortSignal) => {
  try {
    const response = await axios.get(`/api/funds/${fundId}`, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const updateFund = async (fundId: number, payload: UpdateFundPayload, signal?: AbortSignal) => {
  try {
    let config: any = { signal };

    if (payload instanceof FormData) {
      // For FormData, override default Content-Type to let browser set it with boundary
      config.headers = {
        'Content-Type': 'multipart/form-data',
      };
    }

    const response = await axios.put(`/api/funds/${fundId}`, payload, config);
    return response;
  } catch (error) {
    throw error;
  }
};

// Crowdfund API
interface crowdfundingsPayload {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: string;
  isArchived?: string;
}

export const getCrowdfundingList = async (payload?: Partial<crowdfundingsPayload>, signal?: AbortSignal) => {
  try {
    const params = new URLSearchParams();
    if (payload) {
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    const queryString = params.toString();
    const url = queryString ? `/api/crowdfunding?${queryString}` : '/api/crowdfunding';
    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getCrowdfundingById = async (id: number, signal?: AbortSignal) => {
  try {
    const response = await axios.get(`/api/crowdfunding/${id}`, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const updateCrowdfunding = async (id: number, payload: FormData | any, signal?: AbortSignal) => {
  try {
    let config: any = { signal };
    if (payload instanceof FormData) {
      config.headers = { 'Content-Type': 'multipart/form-data' };
    }
    const response = await axios.put(`/api/crowdfunding/${id}`, payload, config);
    return response;
  } catch (error) {
    throw error;
  }
};

export const createCrowdfunding = async (payload: FormData | any, signal?: AbortSignal) => {
  try {
    let config: any = { signal };
    if (payload instanceof FormData) {
      config.headers = { 'Content-Type': 'multipart/form-data' };
    }
    const response = await axios.post('/api/crowdfunding', payload, config);
    return response;
  } catch (error) {
    throw error;
  }
};

// Appeals API
interface appealsPayload {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  appealCategoryId?: string;
  days?: string;
}

export const getAppealsList = async (payload?: Partial<appealsPayload>, signal?: AbortSignal) => {
  try {
    // Build query parameters from payload
    const params = new URLSearchParams();

    if (payload) {
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }

    const queryString = params.toString();
    console.log('Appeals API Query String:', queryString);
    const url = queryString ? `/api/appeals/list?${queryString}` : '/api/appeals/all-list';

    console.log('Appeals API URL:', url);
    console.log('Appeals API Payload:', payload);

    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

interface BulkAppealStatusUpdatePayload {
  appealIds: number[];
  status: 'active' | 'inactive' | 'archived';
}

export const updateAppealsBulkStatus = async (payload: BulkAppealStatusUpdatePayload, signal?: AbortSignal) => {
  try {
    const url = '/api/appeals/bulk-status';
    console.log('Bulk Status Update API URL:', url);
    console.log('Bulk Status Update Payload:', payload);

    const response = await axios.post(url, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Appeal Categories API
export const getAppealCategories = async (signal?: AbortSignal) => {
  try {
    const response = await axios.get('/api/appeal-categories', { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Create Appeal API
type CreateAppealPayload = {
  name: string;
  description?: string;
  appealCategoryId: number;
};

export const createAppeal = async (payload: CreateAppealPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.post('/api/appeals', payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Get Appeal by ID API
export const getAppealById = async (appealId: number, signal?: AbortSignal) => {
  try {
    const response = await axios.get(`/api/appeals/${appealId}`, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Update Appeal API
type UpdateAppealPayload = {
  name?: string;
  description?: string;
  appealCategoryId?: number;
  campaignId?: number;
  fundId?: number;
  image?: string;
};

export const updateAppeal = async (appealId: number, payload: UpdateAppealPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.put(`/api/appeals/${appealId}`, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Create Campaign API
type CreateCampaignPayload = FormData | {
  name: string;
  description?: string;
  image?: string;
  isActive?: boolean;
  isArchived?: boolean;
};

export const createCampaign = async (payload: CreateCampaignPayload, signal?: AbortSignal) => {
  try {
    let config: any = { signal };

    if (payload instanceof FormData) {
      // For FormData, override default Content-Type to let browser set it with boundary
      config.headers = {
        'Content-Type': 'multipart/form-data',
      };
    }

    const response = await axios.post('/api/campaigns', payload, config);
    return response;
  } catch (error) {
    throw error;
  }
};

// Update Campaign API
type UpdateCampaignPayload = FormData | {
  name?: string;
  description?: string;
  image?: string;
  isActive?: boolean;
  isArchived?: boolean;
  icon?: string;
  isDefault?: boolean;
};

export const getCampaignById = async (campaignId: number, signal?: AbortSignal) => {
  try {
    const response = await axios.get(`/api/campaigns/${campaignId}`, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const updateCampaign = async (campaignId: number, payload: UpdateCampaignPayload, signal?: AbortSignal) => {
  try {
    let config: any = { signal };

    if (payload instanceof FormData) {
      // For FormData, override default Content-Type to let browser set it with boundary
      config.headers = {
        'Content-Type': 'multipart/form-data',
      };
    }

    const response = await axios.put(`/api/campaigns/${campaignId}`, payload, config);
    return response;
  } catch (error) {
    throw error;
  }
};

// Create Club API
type CreateClubPayload = FormData | {
  name: string;
  description?: string;
  image?: string;
  desktopImage?: string;
  checkoutImage?: string;
  campaignAndAmountMobile?: string;
  selectedFunds?: number[];
  frequency?: Array<{ name: string; days: number }>;
  startDate?: string;
  endDate?: string;
  split?: any;
  isActive?: boolean;
  isArchived?: boolean;
};

export const createClub = async (payload: CreateClubPayload, signal?: AbortSignal) => {
  try {
    let config: any = { signal };

    if (payload instanceof FormData) {
      // For FormData, override default Content-Type to let browser set it with boundary
      config.headers = {
        'Content-Type': 'multipart/form-data',
      };
    }

    const response = await axios.post('/api/clubs', payload, config);
    return response;
  } catch (error) {
    throw error;
  }
};

// Get Club by ID API
export const getClubById = async (clubId: number, signal?: AbortSignal) => {
  try {
    const response = await axios.get(`/api/clubs/${clubId}`, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Update Club API
type UpdateClubPayload = FormData | {
  name?: string;
  description?: string;
  image?: string;
  desktopImage?: string;
  checkoutImage?: string;
  campaignAndAmountMobile?: string;
  selectedFunds?: number[];
  frequency?: Array<{ name: string; days: number }>;
  startDate?: string;
  endDate?: string;
  split?: any;
  isActive?: boolean;
  isArchived?: boolean;
};

export const updateClub = async (clubId: number, payload: UpdateClubPayload, signal?: AbortSignal) => {
  try {
    let config: any = { signal };

    if (payload instanceof FormData) {
      // For FormData, override default Content-Type to let browser set it with boundary
      config.headers = {
        'Content-Type': 'multipart/form-data',
      };
    }

    const response = await axios.put(`/api/clubs/${clubId}`, payload, config);
    return response;
  } catch (error) {
    throw error;
  }
};

// Get Transaction Payment Info API
export const getTransactionPaymentInfo = async (transactionId: string, signal?: AbortSignal) => {
  try {
    const url = `/api/transactions/${transactionId}`;
    console.log('Transaction Payment Info API URL:', url);
    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Add Donor API
interface AddDonorPayload {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  summary?: string;
  notes?: string;
  phoneNumber?: string;
  userType?: 'individual' | 'organization';
}

export const addDonor = async (payload: AddDonorPayload, signal?: AbortSignal) => {
  try {
    const url = '/api/users/add-donor';
    console.log('Add Donor API URL:', url);
    console.log('Add Donor Payload:', { ...payload, password: '***', confirmPassword: '***' });
    const response = await axios.post(url, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Events API
interface eventsPayload {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  days?: string;
  campaignId?: string;
  sortBy?: string;
  sortOrder?: string;
}

export const getEventsList = async (payload?: Partial<eventsPayload>, signal?: AbortSignal) => {
  try {
    // Build query parameters from payload
    const params = new URLSearchParams();

    if (payload) {
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `/api/events/list?${queryString}` : '/api/events/list';

    console.log('Events API URL:', url);
    console.log('Events API Payload:', payload);

    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

interface BulkEventStatusUpdatePayload {
  eventIds: number[];
  status: 'active' | 'inactive' | 'archived';
}

export const updateEventsBulkStatus = async (payload: BulkEventStatusUpdatePayload, signal?: AbortSignal) => {
  try {
    const url = '/api/events/bulk-status';
    console.log('Bulk Status Update API URL:', url);
    console.log('Bulk Status Update Payload:', payload);

    const response = await axios.post(url, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Create Event API
type CreateEventPayload = FormData | {
  name: string;
  description?: string;
  venue: string;
  eventType: 'free' | 'paid';
  price: number;
  totalQuantity: number;
  allowedVolunteers?: number;
  campaignId: number;
  startDate: string;
  endDate: string;
  isActive?: boolean;
  isArchived?: boolean;
};

export const createEvent = async (payload: CreateEventPayload, signal?: AbortSignal) => {
  try {
    let config: any = { signal };

    if (payload instanceof FormData) {
      // For FormData, override default Content-Type to let browser set it with boundary
      config.headers = {
        'Content-Type': 'multipart/form-data',
      };
    }

    const response = await axios.post('/api/events', payload, config);
    return response;
  } catch (error) {
    throw error;
  }
};

// Get Event by ID API
export const getEventById = async (eventId: number, signal?: AbortSignal) => {
  try {
    const response = await axios.get(`/api/events/${eventId}`, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Update Event API
type UpdateEventPayload = FormData | {
  name?: string;
  description?: string;
  venue?: string;
  eventType?: 'free' | 'paid';
  price?: number;
  totalQuantity?: number;
  allowedVolunteers?: number;
  campaignId?: number;
  startDate?: string;
  endDate?: string;
  timeZone?: string;
  isActive?: boolean;
  isArchived?: boolean;
};

export const updateEvent = async (eventId: number, payload: UpdateEventPayload, signal?: AbortSignal) => {
  try {
    let config: any = { signal };

    if (payload instanceof FormData) {
      // For FormData, override default Content-Type to let browser set it with boundary
      config.headers = {
        'Content-Type': 'multipart/form-data',
      };
    }

    const response = await axios.put(`/api/events/${eventId}`, payload, config);
    return response;
  } catch (error) {
    throw error;
  }
};

// Get Event Donors API
interface eventDonorsPayload {
  page?: number;
  limit?: number;
  search?: string;
}

export const getEventDonors = async (eventId: number, payload?: Partial<eventDonorsPayload>, signal?: AbortSignal) => {
  try {
    // Build query parameters from payload
    const params = new URLSearchParams();

    if (payload) {
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `/api/events/${eventId}/donors?${queryString}` : `/api/admin-events/${eventId}/donors`;

    console.log('Event Donors API URL:', url);
    console.log('Event Donors Payload:', payload);

    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Remove Donors from Event API
interface RemoveDonorsPayload {
  eventId: number;
  userIds: number[];
}

export const removeDonorsFromEvent = async (payload: RemoveDonorsPayload, signal?: AbortSignal) => {
  try {
    const url = '/api/events/remove-donors';
    console.log('Remove Donors API URL:', url);
    console.log('Remove Donors Payload:', payload);

    const response = await axios.post(url, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// ========== TICKET PACK API FUNCTIONS ==========

export interface CreateTicketPackPayload {
  name: string;
  description?: string;
  ticketCount: number;
  price?: number;
  isActive?: boolean;
}

export interface UpdateTicketPackPayload {
  name?: string;
  description?: string;
  ticketCount?: number;
  price?: number;
  isActive?: boolean;
}

export const getTicketPacks = async (eventId: number, signal?: AbortSignal) => {
  try {
    const url = `/api/events/${eventId}/ticket-packs`;
    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const createTicketPack = async (eventId: number, payload: CreateTicketPackPayload, signal?: AbortSignal) => {
  try {
    const url = `/api/events/${eventId}/ticket-packs`;
    const response = await axios.post(url, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const updateTicketPack = async (packId: number, payload: UpdateTicketPackPayload, signal?: AbortSignal) => {
  try {
    const url = `/api/events/ticket-packs/${packId}`;
    const response = await axios.put(url, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const deleteTicketPack = async (packId: number, signal?: AbortSignal) => {
  try {
    const url = `/api/events/ticket-packs/${packId}`;
    const response = await axios.delete(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getAdminDashboardOverallStats = async (signal?: AbortSignal) => {
  try {
    const response = await axios.get('/api/admin-dashboard/overall-stats', { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getAdminDashboardDonationTrends = async (startDate?: string, endDate?: string, signal?: AbortSignal) => {
  try {
    const params = new URLSearchParams();

    if (startDate) {
      params.append('startDate', startDate);
    }

    if (endDate) {
      params.append('endDate', endDate);
    }

    const queryString = params.toString();
    const url = queryString ? `/api/admin-dashboard/donation-trends?${queryString}` : '/api/admin-dashboard/donation-trends';

    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getAdminDashboardFundDistribution = async (startDate?: string, endDate?: string, signal?: AbortSignal) => {
  try {
    const params = new URLSearchParams();

    if (startDate) {
      params.append('startDate', startDate);
    }

    if (endDate) {
      params.append('endDate', endDate);
    }

    const queryString = params.toString();
    const url = queryString ? `/api/admin-dashboard/fund-distribution?${queryString}` : '/api/admin-dashboard/fund-distribution';

    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getAdminDashboardDonationByType = async (startDate?: string, endDate?: string, signal?: AbortSignal) => {
  try {
    const params = new URLSearchParams();

    if (startDate) {
      params.append('startDate', startDate);
    }

    if (endDate) {
      params.append('endDate', endDate);
    }

    const queryString = params.toString();
    const url = queryString ? `/api/admin-dashboard/donation-by-type?${queryString}` : '/api/admin-dashboard/donation-by-type';

    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getAdminDashboardDonorGrowth = async (startDate?: string, endDate?: string, signal?: AbortSignal) => {
  try {
    const params = new URLSearchParams();

    if (startDate) {
      params.append('startDate', startDate);
    }

    if (endDate) {
      params.append('endDate', endDate);
    }

    const queryString = params.toString();
    const url = queryString ? `/api/admin-dashboard/donor-growth?${queryString}` : '/api/admin-dashboard/donor-growth';

    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getAdminDashboardCampaignPerformance = async (startDate?: string, endDate?: string, signal?: AbortSignal) => {
  try {
    const params = new URLSearchParams();

    if (startDate) {
      params.append('startDate', startDate);
    }

    if (endDate) {
      params.append('endDate', endDate);
    }

    const queryString = params.toString();
    const url = queryString ? `/api/admin-dashboard/campaign-performance?${queryString}` : '/api/admin-dashboard/campaign-performance';

    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const uploadDocument = async (userId: number, formData: FormData, signal?: AbortSignal) => {
  try {
    const url = `/api/users/${userId}/upload-document`;
    console.log('Upload Document API URL:', url);

    const response = await axios.post(url, formData, {
      signal,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const deleteDocument = async (userId: number, documentUrl: string, signal?: AbortSignal) => {
  try {
    const url = `/api/users/${userId}/delete-document`;
    console.log('Delete Document API URL:', url);

    const response = await axios.delete(url, {
      signal,
      data: {
        documentUrl: documentUrl
      },
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getAdminDashboardAppealsPerformance = async (startDate?: string, endDate?: string, signal?: AbortSignal) => {
  try {
    const params = new URLSearchParams();

    if (startDate) {
      params.append('startDate', startDate);
    }

    if (endDate) {
      params.append('endDate', endDate);
    }

    const queryString = params.toString();
    const url = queryString ? `/api/admin-dashboard/appeals-performance?${queryString}` : '/api/admin-dashboard/appeals-performance';

    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getAdminDashboardTopClubs = async (startDate?: string, endDate?: string, signal?: AbortSignal) => {
  try {
    const params = new URLSearchParams();

    if (startDate) {
      params.append('startDate', startDate);
    }

    if (endDate) {
      params.append('endDate', endDate);
    }

    const queryString = params.toString();
    const url = queryString ? `/api/admin-dashboard/top-clubs?${queryString}` : '/api/admin-dashboard/top-clubs';

    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getAdminDashboardRecentDonations = async (limit: number = 10, signal?: AbortSignal) => {
  try {
    const response = await axios.get(`/api/admin-dashboard/recent-donations?limit=${limit}`, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Retry Payment for Overdue Gift API
export const retryGiftPayment = async (giftId: number, signal?: AbortSignal) => {
  try {
    const url = `/api/admin-gifts/${giftId}/retry-payment`;
    console.log('Retry Payment API URL:', url);

    const response = await axios.post(url, {}, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Resend Receipt Email API
export const resendReceipt = async (giftId: number, pdfBase64?: string, signal?: AbortSignal) => {
  try {
    const url = '/api/admin-gifts/resend-receipt';
    console.log('Resend Receipt API URL:', url);

    const response = await axios.post(url, { giftId, pdfBase64 }, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Update Gift Metadata and Notes API
interface UpdateGiftInfoPayload {
  metadata?: string;
  notes?: string;
}

export const updateGiftInfo = async (giftId: number, payload: UpdateGiftInfoPayload, signal?: AbortSignal) => {
  try {
    const url = `/api/admin-gifts/${giftId}/update-info`;
    console.log('Update Gift Info API URL:', url);
    console.log('Update Gift Info Payload:', payload);

    const response = await axios.post(url, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const archiveDonationApi = async (giftId: number, signal?: AbortSignal) => {
  const response = await axios.put(`/api/admin-gifts/${giftId}/archive`, {}, { signal });
  return response.data;
};

export const bulkArchiveDonationsApi = async (receiptIds: number[], signal?: AbortSignal) => {
  const response = await axios.post('/api/admin-gifts/bulk-archive', { receiptIds }, { signal });
  return response.data;
};

// Set attribution (soft-credit or tribute) on a gift
export const setAttribution = async (
  giftId: number,
  payload: { type: 'soft-credit' | 'tribute'; userId: number },
  signal?: AbortSignal
) => {
  const response = await axios.post(`/api/admin-gifts/${giftId}/attribution`, payload, { signal });
  return response;
};

// Remove attribution (soft-credit or tribute) from a gift
export const removeAttribution = async (
  giftId: number,
  payload: { type: 'soft-credit' | 'tribute' },
  signal?: AbortSignal
) => {
  const response = await axios.post(`/api/admin-gifts/${giftId}/attribution-remove`, payload, { signal });
  return response;
};

// Search users for attribution (soft-credit: any; tribute: deceased only)
export const searchUsersForAttribution = async (
  search: string,
  options?: { deceased?: boolean },
  signal?: AbortSignal
) => {
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  if (options?.deceased === true) params.append('deceased', 'true');
  const url = `/api/users/search/user?${params.toString()}`;
  const response = await axios.get(url, { signal });
  return response;
};

// ==================== CMS Pages APIs ====================

// Get all CMS Pages (Admin)
interface CmsPagesPayload {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
}

export const getCmsPages = async (payload?: CmsPagesPayload, signal?: AbortSignal) => {
  try {
    const params = new URLSearchParams();

    if (payload) {
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `/api/cms/admin/pages?${queryString}` : '/api/cms/admin/pages';

    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Get CMS Page Sections by Page ID
export const getCmsPageSections = async (pageId: number, signal?: AbortSignal) => {
  try {
    const response = await axios.get(`/api/cms/admin/pages/${pageId}/sections`, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Get CMS Template Pages
export const getCmsTemplates = async (search?: string, signal?: AbortSignal) => {
  try {
    const params = new URLSearchParams();
    if (search && search.trim() !== '') {
      params.append('search', search.trim());
    }
    const queryString = params.toString();
    const url = queryString ? `/api/cms/admin/pages/templates?${queryString}` : '/api/cms/admin/pages/templates';
    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Create Page from Template
export interface CreatePageFromTemplatePayload {
  pageId: number;
  name: string;
  slug?: string;
  title: string;
  description: string;
  linkType?: 'link' | 'fund' | 'crowdfunding' | 'campaign' | 'appeal' | 'club' | 'external';
  fundId?: number;
  crowdfundingId?: number;
  campaignId?: number;
  appealId?: number;
  clubId?: number;
}

export const createPageFromTemplate = async (payload: CreatePageFromTemplatePayload) => {
  try {
    const response = await axios.post('/api/cms/admin/pages/create-from-template', payload);
    return response;
  } catch (error) {
    throw error;
  }
};

// Get CMS Images by Folder
export const getCmsImagesByFolder = async (folder: string, signal?: AbortSignal) => {
  try {
    const response = await axios.get(`/api/cms/admin/pages/list-images/${folder}`, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Upload CMS Image
export const uploadCmsImage = async (
  file: File,
  folder: string,
  name: string,
  oldImageUrl?: string
) => {
  try {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('folder', folder);
    formData.append('name', name);
    if (oldImageUrl) {
      formData.append('oldImageUrl', oldImageUrl);
    }

    const response = await axios.post('/api/cms/admin/pages/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// Update CMS Page Sections
export const updateCmsPageSections = async (
  pageId: number,
  data: {
    page?: {
      name?: string;
      slug?: string;
      title?: string;
      description?: string;
    };
    sections?: any[];
  }
) => {
  try {
    const response = await axios.put(`/api/cms/admin/pages/${pageId}/sections`, data);
    return response;
  } catch (error) {
    throw error;
  }
};

// Delete CMS Page
export const deleteCmsPage = async (pageId: number, signal?: AbortSignal) => {
  try {
    const response = await axios.delete(`/api/cms/admin/pages/${pageId}`, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Update Fund Types (Add/Delete donate type)
export const updateFundTypes = async (fundIds: number[], action: 'add' | 'delete') => {
  try {
    const response = await axios.post('/api/cms/admin/pages/funds/type', {
      fundIds,
      action,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// Reorder Funds
export const reorderFunds = async (fundIds: number[]) => {
  try {
    const response = await axios.post('/api/cms/admin/pages/funds/reorder', {
      fundIds,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// Create Slide for Section
export interface CreateSlidePayload {
  name?: string;
  content: Record<string, {
    value: string;
    href?: string | null;
    type?: string | null;
    fundId?: number | null;
    crowdfundingId?: number | null;
    campaignId?: number | null;
    clubId?: number | null;
    orderNo: number;
    isActive: boolean;
  }>;
  images: {
    desktop: string;
    tablet: string;
    mobile: string;
  };
  gradients?: {
    desktop?: string | null;
    tablet?: string | null;
    mobile?: string | null;
  };
  dimensions?: any;
  isActive?: boolean;
}

export const createSlide = async (sectionId: number, payload: CreateSlidePayload, signal?: AbortSignal) => {
  try {
    const response = await axios.post(`/api/cms/admin/pages/sections/${sectionId}/slides`, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Delete Slide
export const deleteSlide = async (slideId: number, signal?: AbortSignal) => {
  try {
    const response = await axios.delete(`/api/cms/admin/pages/slides/${slideId}`, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Get CRM Funds (donate type funds)
export const getCrmFunds = async (signal?: AbortSignal) => {
  try {
    const response = await axios.get('/api/funds/crm-funds', { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Get Club Donations (Gifts) API
interface clubDonationsPayload {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
}

export const getClubDonations = async (clubId: number, payload?: Partial<clubDonationsPayload>, signal?: AbortSignal) => {
  try {
    // Build query parameters from payload
    const params = new URLSearchParams();

    if (payload) {
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `/api/admin-clubs/${clubId}/donations?${queryString}` : `/api/admin-clubs/${clubId}/donations`;

    console.log('Club Donations API URL:', url);
    console.log('Club Donations Payload:', payload);

    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// ==================== Navbar CMS APIs ====================

// Get navbar structure (public - for frontend)
export const getNavbar = async (signal?: AbortSignal) => {
  try {
    const response = await axios.get('/api/cms/navbar', { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// ==================== Announcement Bar CMS APIs ====================

export interface AnnouncementItem {
  text: string;
  href: string | null;
}

export interface AnnouncementBarPayload {
  items: AnnouncementItem[];
  isActive?: boolean;
}

export const getAnnouncementBar = async (signal?: AbortSignal) => {
  try {
    const response = await axios.get('/api/cms/announcement', { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const upsertAnnouncementBar = async (payload: AnnouncementBarPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.post('/api/cms/announcement', payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Get navbar structure with IDs (admin - for CMS editing)
export const getNavbarAdmin = async (signal?: AbortSignal) => {
  try {
    const response = await axios.get('/api/cms/navbar/admin', { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Bulk create navbar structure
export interface NavbarBulkCreatePayload {
  items: Array<{
    label: string;
    href?: string;
    icon?: string;
    hasDropdown?: boolean;
    isBold?: boolean;
    linkType?: 'link' | 'fund' | 'crowdfunding' | 'campaign' | 'appeal' | 'external';
    fundId?: number;
    crowdfundingId?: number;
    campaignId?: number;
    appealId?: number;
    dropdownItems?: Array<any> | Record<string, Array<any>>;
  }>;
  clearExisting?: boolean;
}

export const bulkCreateNavbar = async (payload: NavbarBulkCreatePayload, signal?: AbortSignal) => {
  try {
    const response = await axios.post('/api/cms/navbar/bulk-create', payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Create navbar item
export interface CreateNavbarItemPayload {
  label: string;
  href?: string;
  icon?: string;
  hasDropdown?: boolean;
  orderNo?: number;
  isActive?: boolean;
  isBold?: boolean;
  linkType?: 'link' | 'fund' | 'crowdfunding' | 'campaign' | 'appeal' | 'club' | 'external';
  fundId?: number;
  crowdfundingId?: number;
  campaignId?: number;
  appealId?: number;
  clubId?: number;
}

export const createNavbarItem = async (payload: CreateNavbarItemPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.post('/api/cms/navbar/items', payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Update navbar item
export interface UpdateNavbarItemPayload extends Partial<CreateNavbarItemPayload> { }

export const updateNavbarItem = async (id: number, payload: UpdateNavbarItemPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.post(`/api/cms/navbar/items/${id}/edit`, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Toggle navbar item active status
export const toggleNavbarItemActive = async (id: number, signal?: AbortSignal) => {
  try {
    const response = await axios.post(`/api/cms/navbar/items/${id}/toggle-active`, {}, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Reorder navbar items
export interface ReorderNavbarItemsPayload {
  items: Array<{ id: number; orderNo: number }>;
}

export const reorderNavbarItems = async (payload: ReorderNavbarItemsPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.post('/api/cms/navbar/items/reorder', payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Create dropdown group
export interface CreateDropdownGroupPayload {
  navbarItemId: number;
  label: string;
  orderNo?: number;
  isActive?: boolean;
}

export const createDropdownGroup = async (payload: CreateDropdownGroupPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.post('/api/cms/navbar/groups', payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Update dropdown group
export interface UpdateDropdownGroupPayload {
  label?: string;
  orderNo?: number;
  isActive?: boolean;
  navbarItemId?: number;
}

export const updateDropdownGroup = async (id: number, payload: UpdateDropdownGroupPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.post(`/api/cms/navbar/groups/${id}/edit`, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Create dropdown item
export interface CreateDropdownItemPayload {
  navbarItemId: number;
  label: string;
  href: string;
  groupId?: number;
  parentItemId?: number;
  icon?: string;
  orderNo?: number;
  isActive?: boolean;
  linkType?: 'link' | 'fund' | 'crowdfunding' | 'campaign' | 'appeal' | 'club' | 'external';
  fundId?: number;
  crowdfundingId?: number;
  campaignId?: number;
  appealId?: number;
  clubId?: number;
}

export const createDropdownItem = async (payload: CreateDropdownItemPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.post('/api/cms/navbar/dropdown-items', payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Update dropdown item
export interface UpdateDropdownItemPayload extends Partial<CreateDropdownItemPayload> {
  navbarItemId?: number;
}

export const updateDropdownItem = async (id: number, payload: UpdateDropdownItemPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.post(`/api/cms/navbar/dropdown-items/${id}/edit`, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Reorder dropdown items
export interface ReorderDropdownItemsPayload {
  items: Array<{ id: number; orderNo: number }>;
}

export const reorderDropdownItems = async (payload: ReorderDropdownItemsPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.post('/api/cms/navbar/dropdown-items/reorder', payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Delete navbar item
export const deleteNavbarItem = async (id: number, signal?: AbortSignal) => {
  try {
    const response = await axios.delete(`/api/cms/navbar/items/${id}`, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Delete dropdown item
export const deleteDropdownItem = async (id: number, signal?: AbortSignal) => {
  try {
    const response = await axios.delete(`/api/cms/navbar/dropdown-items/${id}`, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Delete dropdown group
export const deleteDropdownGroup = async (id: number, signal?: AbortSignal) => {
  try {
    const response = await axios.delete(`/api/cms/navbar/groups/${id}`, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// ==================== Footer CMS APIs ====================

// Get footer data
export const getFooter = async (signal?: AbortSignal) => {
  try {
    const response = await axios.get('/api/cms/footer', { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Upsert footer (create or update)
export interface FooterSocialLinkInput {
  platform: string;
  url: string;
  icon?: string;
  orderNo?: number;
  isActive?: boolean;
}

export interface FooterLinkInput {
  label: string;
  href: string;
  orderNo?: number;
  isActive?: boolean;
}

export interface FooterUpsertPayload {
  newsletterText1?: string;
  newsletterText2?: string;
  description?: string;
  contactEmail?: string;
  contactPhone?: string;
  officeAddress?: string;
  facebookUrl?: string;
  linkedinUrl?: string;
  youtubeUrl?: string;
  instagramUrl?: string;
  copyrightText?: string;
  usefulLinks?: FooterLinkInput[];
  socialLinks?: FooterSocialLinkInput[];
}

export const upsertFooter = async (payload: FooterUpsertPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.post('/api/cms/footer', payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Import Donations from CSV
interface ImportDonationItem {
  firstName: string;
  lastName: string;
  email: string;
  amount: number | string;
  fundIds: string;
  fundAmounts: string;
  currency: string;
  date: string;
  donorType?: string;
}

interface ImportDonationsPayload {
  donations: ImportDonationItem[];
  sendEmail?: boolean;
}

export const importDonations = async (payload: ImportDonationsPayload, signal?: AbortSignal) => {
  try {
    const url = '/api/crmDonation/import-donations';
    console.log('Import Donations API URL:', url);
    console.log('Import Donations Payload:', payload);

    const response = await axios.post(url, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// ==================== Prospects APIs ====================

// Get all prospects with filters
export interface GetProspectsPayload {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  userType?: string;
  assignedTo?: number;
  userId?: number;
  minLinkageRating?: number;
  maxLinkageRating?: number;
  minAbilityRating?: number;
  maxAbilityRating?: number;
  minInterestRating?: number;
  maxInterestRating?: number;
  sortBy?: string;
  sortOrder?: string;
}

export const getProspectsList = async (params: GetProspectsPayload = {}, signal?: AbortSignal) => {
  try {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.userType) queryParams.append('userType', params.userType);
    if (params.assignedTo) queryParams.append('assignedTo', params.assignedTo.toString());
    if (params.userId) queryParams.append('userId', params.userId.toString());
    if (params.minLinkageRating) queryParams.append('minLinkageRating', params.minLinkageRating.toString());
    if (params.maxLinkageRating) queryParams.append('maxLinkageRating', params.maxLinkageRating.toString());
    if (params.minAbilityRating) queryParams.append('minAbilityRating', params.minAbilityRating.toString());
    if (params.maxAbilityRating) queryParams.append('maxAbilityRating', params.maxAbilityRating.toString());
    if (params.minInterestRating) queryParams.append('minInterestRating', params.minInterestRating.toString());
    if (params.maxInterestRating) queryParams.append('maxInterestRating', params.maxInterestRating.toString());
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const url = `/api/prospects?${queryParams.toString()}`;
    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Create a new prospect
export interface CreateProspectPayload {
  userId: number;
  status?: string;
  assignedTo?: number;
  linkageRating?: number;
  linkageNotes?: string;
  abilityRating?: number;
  abilityNotes?: string;
  interestRating?: number;
  interestNotes?: string;
}

export const createProspect = async (payload: CreateProspectPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.post('/api/prospects', payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Get a single prospect by ID
export const getProspectById = async (prospectId: number, signal?: AbortSignal) => {
  try {
    const response = await axios.get(`/api/prospects/${prospectId}`, { signal });
    return { data: response.data.prospect };
  } catch (error) {
    throw error;
  }
};

// Get all donors for prospect selection
export const getDonorsForProspect = async (search: string = '', signal?: AbortSignal) => {
  try {
    const queryParams = new URLSearchParams();
    if (search) queryParams.append('search', search);

    const url = `/api/users/search/user?${queryParams.toString()}`;
    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Get proposals for a prospect
export interface GetProposalsParams {
  status?: string;
  type?: string;
  sortBy?: string;
  sortOrder?: string;
}

export const getProspectProposals = async (prospectId: number, params: GetProposalsParams = {}, signal?: AbortSignal) => {
  try {
    const queryParams = new URLSearchParams();

    if (params.status) queryParams.append('status', params.status);
    if (params.type) queryParams.append('type', params.type);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const queryString = queryParams.toString();
    const url = `/api/prospects/${prospectId}/proposals${queryString ? `?${queryString}` : ''}`;
    const response = await axios.get(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Create a new proposal
export interface CreateProposalPayload {
  prospectId: number;
  name: string;
  type: string;
  status?: string;
  assignedTo?: number;
  estimatedAskValue?: number;
  proposedAskValue?: number;
  acceptedAskValue?: number;
  dateAsked?: string;
  dateProposalSigned?: string;
  nextStewardshipDate?: string;
  lastStewardshipDate?: string;
  notes?: string;
}

export const createProposal = async (payload: CreateProposalPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.post('/api/prospects/proposals', payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Create pledge gifts for a proposal
export interface CreateProposalGiftsPayload {
  gifts: Array<{
    amount: number;
    date: string;
  }>;
  scheduledDonationId?: number; // Optional - if provided, adds gifts to existing scheduled donation
}

export const createProposalGifts = async (proposalId: number, payload: CreateProposalGiftsPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.post(`/api/prospects/proposals/${proposalId}/gifts`, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Update a gift for a proposal
export interface UpdateGiftPayload {
  amount?: number;
  date?: string;
  status?: 'completed' | 'pending' | 'cancelled' | 'overdue' | 'paused' | 'refunded';
}

export const updateProposalGift = async (proposalId: number, giftId: number, payload: UpdateGiftPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.patch(`/api/prospects/proposals/${proposalId}/gifts/${giftId}`, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Update a proposal
export interface UpdateProposalPayload {
  name?: string;
  status?: 'cultivation' | 'solicitation' | 'stewardship' | 'rejected' | 'completed';
  type?: 'event_sponsorship' | 'scholarship' | 'capital_funding' | 'program_funding' | 'bequest_waqf' | 'grant';
  assignedTo?: number | null;
  estimatedAskValue?: number | null;
  proposedAskValue?: number | null;
  acceptedAskValue?: number | null;
  dateAsked?: string | null;
  dateProposalSigned?: string | null;
  nextStewardshipDate?: string | null;
  lastStewardshipDate?: string | null;
  notes?: string | null;
}

export const updateProposal = async (proposalId: number, payload: UpdateProposalPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.patch(`/api/prospects/proposals/${proposalId}`, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Update a prospect
export interface UpdateProspectPayload {
  status?: 'no_assessment' | 'not_a_prospect' | 'prospect' | 'identification' | 'qualification' | 'active_donor';
  assignedTo?: number | null;
  linkageRating?: number | null;
  linkageNotes?: string | null;
  abilityRating?: number | null;
  abilityNotes?: string | null;
  interestRating?: number | null;
  interestNotes?: string | null;
}

export const updateProspect = async (prospectId: number, payload: UpdateProspectPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.patch(`/api/prospects/${prospectId}`, payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Create payment intent for CRM
export interface CreatePaymentIntentCRMPayload {
  userId: number;
}

export interface CreatePaymentIntentCRMResponse {
  userId: number;
  customerId: string;
  setupIntentId: string;
  clientSecret: string;
}

export const createPaymentIntentCRM = async (payload: CreatePaymentIntentCRMPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.post<{ message: string; response: CreatePaymentIntentCRMResponse }>('/api/payment-intent/crm', payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Create proposal gift (new endpoint)
export interface CreateProposalGiftPayload {
  proposalId: number;
  gift: {
    type: 'pledge' | 'bequest' | 'giftInKind';
    amount: number;
    currency?: string;
    fundId: number;
    isNewPaymentMethod: boolean;
    paymentMethodId: string;
    scheduledDate?: string;
    timeZone?: string;
    appealId?: number;
  };
  scheduledDonationId?: number;
}

export const createProposalGift = async (payload: CreateProposalGiftPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.post('/api/prospects/proposals/gift', payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Update proposal gift (new endpoint)
export interface UpdateProposalGiftPayload {
  giftId: number;
  proposalId: number;
  amount?: number;
  scheduledDate?: string;
  timeZone?: string;
  paymentMethodId?: string;
  fundId?: number;
  status?: 'scheduled' | 'cancelled' | 'overdue' | 'paused' | 'refunded';
  productName?: string;
}

export const updateProposalGiftNew = async (payload: UpdateProposalGiftPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.put('/api/prospects/proposals/gift', payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Get gift details by ID
export const getGiftById = async (giftId: number, signal?: AbortSignal) => {
  try {
    const response = await axios.get(`/api/admin-gifts/${giftId}`, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Get payment methods for CRM
export interface PaymentMethod {
  id: string;
  type: string;
  card: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
    funding: string;
  } | null;
  created: number;
  customer: string;
}

export interface GetPaymentMethodsResponse {
  paymentMethods: PaymentMethod[];
}

export const getPaymentMethodsCRM = async (userId: number, signal?: AbortSignal) => {
  try {
    // Backend GET route expects userId in body (unusual but matches backend implementation)
    const response = await axios.post('/api/payment-methods/crm', { userId }, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Check or create user
export interface CheckOrCreateUserPayload {
  firstName: string;
  lastName: string;
  email: string;
  userType?: string;
  phoneNumber?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
}

export interface CheckOrCreateUserResponse {
  message: string;
  isNewUser: boolean;
  user: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    isVerified: boolean;
    isActive: boolean;
    createdAt: string;
  };
}

export const checkOrCreateUser = async (payload: CheckOrCreateUserPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.post<CheckOrCreateUserResponse>('/api/crmDonation/user', payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Create payment intent for on-session CRM (one-time donations)
export interface CreateOnSessionPaymentIntentCRMPayload {
  userId: number;
  gift: {
    amount: number;
    currency: string;
    fundId: number;
    type: 'one-time' | 'recurring';
    scheduledDate?: string;
    timeZone: string;
    frequency?: {
      name: string;
      days: number;
    };
    appealId?: number;
    subtype?: string;
    giftType?: string;
    transactionId?: string;
    productName?: string;
    isZakat?: boolean;
    isSadaqah?: boolean;
    isQurban?: boolean;
    notes?: string;
    metadata?: string;
    paymentGateway?: string;
    softCreditUserId?: number;
    tributeUserId?: number;
    tributeName?: string;
  };
  appealId?: number;
}

export interface CreateOnSessionPaymentIntentCRMResponse {
  clientSecret: string;
}

export const createOnSessionPaymentIntentCRM = async (payload: CreateOnSessionPaymentIntentCRMPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.post<{ clientSecret: string }>('/api/payment-intent/on-session/crm', payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

// Create gift without proposal
export interface CreateGiftWithoutProposalPayload {
  userId: number;
  gift: {
    amount: number;
    currency?: string;
    fundId: number;
    isNewPaymentMethod?: boolean;
    paymentMethodId?: string;
    scheduledDate?: string;
    timeZone?: string;
    appealId?: number;
    frequency?: {
      name: string;
      days: number;
    };
    subtype?: string;
    giftType?: string;
    transactionId?: string;
    productName?: string;
    isZakat?: boolean;
    isSadaqah?: boolean;
    isQurban?: boolean;
    notes?: string;
    metadata?: string;
    paymentGateway?: string;
    softCreditUserId?: number;
    tributeUserId?: number;
    tributeName?: string;
    status?: string;
  };
}

export const createGiftWithoutProposal = async (payload: CreateGiftWithoutProposalPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.post('/api/crmDonation/gift', payload, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export const deleteDonor = async (id: number, forceCancel: boolean = false, signal?: AbortSignal) => {
  try {
    const url = `/api/users/${id}${forceCancel ? '?forceCancel=true' : ''}`;
    const response = await axios.delete(url, { signal });
    return response;
  } catch (error) {
    throw error;
  }
};

export interface TaxReceiptHistoryParams {
  page?: number;
  limit?: number;
  type?: string;
  from?: string;
  to?: string;
  financialYear?: string;
  status?: string;
  search?: string;
  userId?: number;
}

export const getTaxReceiptHistoryApi = async (params?: TaxReceiptHistoryParams, signal?: AbortSignal) => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.type) queryParams.append('type', params.type);
    if (params?.from) queryParams.append('from', params.from);
    if (params?.to) queryParams.append('to', params.to);
    if (params?.financialYear) queryParams.append('financialYear', params.financialYear);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.userId) queryParams.append('userId', params.userId.toString());

    const response = await axios.get(`/api/gifts/financial-year?${queryParams.toString()}`, { signal });
    return response.data;
  } catch (err) {
    throw err;
  }
};

export interface EOFYStatementParams {
  from: string;
  to: string;
  page?: number;
  limit?: number;
  search?: string;
}

export const getEOFYStatements = async (params: EOFYStatementParams, signal?: AbortSignal) => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('from', params.from);
    queryParams.append('to', params.to);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);

    const response = await axios.get(`/api/admin-gifts/eof-statements?${queryParams.toString()}`, { signal });
    return response.data;
  } catch (err) {
    throw err;
  }
};

export interface SendEOFYStatementPayload {
  userId: number;
  email: string;
  financialYear: string;
  pdfBase64: string;
  firstName: string;
  totalAmount: number;
}

export const sendEOFYStatement = async (payload: SendEOFYStatementPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.post('/api/admin-gifts/send-eof-statement', payload, { signal });
    return response.data;
  } catch (err) {
    throw err;
  }
};

export interface QueueEOFYStatementsPayload {
  financialYear: string;
  subject?: string;
  recipients: Array<{
    email: string;
    firstName: string;
    lastName?: string;
    totalAmount: number;
    pdfBase64: string;
  }>;
}

export const queueEOFYStatements = async (payload: QueueEOFYStatementsPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.post('/api/admin-gifts/queue-eof-statements', payload, { signal });
    return response.data;
  } catch (err) {
    throw err;
  }
};

export interface CreateStaffPayload {
  firstName: string;
  lastName: string;
  email: string;
  password?: string;
  role: 'donorCare' | 'finance' | 'fundraising' | 'marketing' | 'global' | 'programs';
  isActive?: boolean;
}

export const getCrmStaffList = async (signal?: AbortSignal) => {
  try {
    const response = await axios.get('/api/users/crm-staff', { signal });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createCrmStaff = async (payload: CreateStaffPayload, signal?: AbortSignal) => {
  try {
    const response = await axios.post('/api/users/crm-staff', payload, { signal });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateCrmStaff = async (id: number, payload: Partial<CreateStaffPayload>, signal?: AbortSignal) => {
  try {
    const response = await axios.put(`/api/users/crm-staff/${id}`, payload, { signal });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const mergeUsers = async (primaryUserId: number, secondaryUserId: number, copyPaymentGateway: boolean = false) => {
  try {
    const response = await axios.post('/api/users/merge', {
      primaryUserId,
      secondaryUserId,
      copyPaymentGateway
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const checkMerge = async (primaryUserId: number, secondaryUserId: number) => {
  try {
    const response = await axios.post('/api/users/merge/precheck', {
      primaryUserId,
      secondaryUserId
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};
